import React, { useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import {
  InformationCircleIcon,
  CheckIcon,
  ChevronUpDownIcon,
  MapPinIcon,
  SparklesIcon,
} from "@heroicons/react/20/solid";

function ProfileInput({
  location,
  locations,
  onLocationChange,
  onSubmit: onSubmitProp,
  isLoading,
  error,
  className = "",
  isProfileComplete,
  savedProfileText,
}) {
  const isSubmitDisabled = () => {
    if (isLoading || !location) return true;
    return !isProfileComplete;
  };

  const handleSubmitClick = async (e) => {
    e.preventDefault();
    const locationToSubmit = location;
    let textToSubmit = null;
    let sourceInfo = null;

    if (!locationToSubmit) {
      alert("Please select a location.");
      return;
    }

    if (!isProfileComplete) {
      alert("Profile not complete. Cannot use this option.");
      return;
    }
    textToSubmit = savedProfileText || null;

    // ADD THESE CONSOLE LOGS HERE
    console.log("=== Profile Text Debug ===");
    console.log("Profile text being sent:", textToSubmit);
    console.log("Profile text length:", textToSubmit?.length || 0);
    console.log("First 500 chars of profile:", textToSubmit?.substring(0, 500));
    console.log("Profile text type:", typeof textToSubmit);
    console.log("========================");

    sourceInfo = {
      type: "profile",
      title: "Saved User Profile",
      skipResumeBuilderIntent: false,
    };

    if (locationToSubmit && sourceInfo && onSubmitProp) {
      onSubmitProp(textToSubmit, locationToSubmit, sourceInfo);
    }
  };

  const selectedLocationObject = locations.find(
    (loc) => loc.value === location
  );

  return (
    <div className={`max-w-xl mx-auto ${className}`}>
      {/* Simplified Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-3 mb-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background:
                "linear-gradient(135deg, rgb(7, 50, 20), rgb(5, 35, 14))",
            }}
          >
            <SparklesIcon className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            AI Career Analysis
          </h2>
        </div>
        <p className="text-sm text-gray-600">
          Discover your perfect career path with AI insights
        </p>
      </div>

      {/* Clean Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="space-y-5">
          {/* Location Selection */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <MapPinIcon className="w-4 h-4 text-gray-500" />
              Target Location
            </label>

            <Listbox
              value={location}
              onChange={onLocationChange}
              disabled={isLoading}
            >
              <div className="relative">
                <Listbox.Button className="relative w-full bg-white border border-gray-300 rounded-md px-3 py-2.5 text-left focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed text-sm">
                  <span className="block truncate font-medium white">
                    {selectedLocationObject?.name ||
                      "Select your target location"}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronUpDownIcon className="w-4 h-4 text-gray-400" />
                  </span>
                </Listbox.Button>

                <Transition
                  as={React.Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                    {(locations || []).map((loc) => (
                      <Listbox.Option
                        key={loc.value}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2.5 pl-9 pr-4 ${
                            active
                              ? "bg-green-50 text-green-900"
                              : "text-gray-900"
                          }`
                        }
                        value={loc.value}
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate ${
                                selected ? "font-semibold" : "font-normal"
                              }`}
                            >
                              {loc.name}
                            </span>
                            {selected && (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-green-600">
                                <CheckIcon className="w-4 h-4" />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>

          {/* Submit Button */}
          <button
            type="button"
            onClick={handleSubmitClick}
            disabled={isSubmitDisabled()}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md font-semibold text-white text-sm transition-all duration-200 ${
              isSubmitDisabled()
                ? "bg-gray-300 cursor-not-allowed"
                : "hover:shadow-md hover:-translate-y-0.5"
            }`}
            style={{
              background: isSubmitDisabled()
                ? undefined
                : "linear-gradient(135deg, rgb(7, 50, 20), rgb(5, 35, 14))",
            }}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <SparklesIcon className="w-4 h-4" />
                Analyze & Find Career Paths
              </>
            )}
          </button>

          {/* Error Message */}
          {error && !isLoading && (
            <div className="flex gap-3 p-3 bg-red-50 border border-red-200 rounded-md">
              <InformationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">
                  Analysis Error
                </p>
                <p className="text-sm text-red-700 mt-0.5">{error}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileInput;
